import React from 'react';
import { X, MapPin, Briefcase, DollarSign, Award, Mail, FileText, Users, Calendar } from 'lucide-react';

const JobPreview = ({ job, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-green-100 text-green-800',
      'CLOSED': 'bg-red-100 text-red-800',
      'ON_HOLD': 'bg-yellow-100 text-yellow-800',
      'DRAFT': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'HIGH': 'bg-red-100 text-red-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'LOW': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-3xl h-full flex flex-col animate-slideInRight overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0 bg-white">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">{job.jobTitle}</h2>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(job.jobStatus)}`}>
                {job.jobStatus}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getPriorityColor(job.jobPriority)}`}>
                {job.jobPriority}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Job Code: {job.jobCode}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Basic Information */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Briefcase size={20} style={{ color: "#1C4E6D" }} />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Client</p>
                <p className="text-sm text-gray-900 font-medium">{job.client || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Department</p>
                <p className="text-sm text-gray-900">{job.department || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Number of Openings</p>
                <p className="text-sm text-gray-900">{job.numberOfOpenings || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Assigned To</p>
                <p className="text-sm text-gray-900">{job.assignedTo || 'Unassigned'}</p>
              </div>
            </div>
          </section>

          {/* Job Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MapPin size={20} className="text-green-600" />
              Job Details
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Work Model</p>
                <p className="text-sm text-gray-900">{job.workModel || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Job Type</p>
                <p className="text-sm text-gray-900">{job.jobType || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Work Location</p>
                <p className="text-sm text-gray-900">{job.workLocation || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Working Hours</p>
                <p className="text-sm text-gray-900">{job.workingHours || 'N/A'}</p>
              </div>
            </div>

            {/* Job Locations */}
            {job.jobLocation?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Job Locations</p>
                <div className="flex flex-wrap gap-2">
                  {(Array.isArray(job.jobLocation) ? job.jobLocation : [job.jobLocation]).map((location, idx) => (
                    <span key={idx} className="inline-block px-3 py-1 text-xs rounded-full border" style={{ backgroundColor: "#e8f4f9", color: "#1C4E6D", borderColor: "#1C4E6D" }}>
                      {location}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Experience & Skills */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Award size={20} className="text-yellow-600" />
              Experience & Skills
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Experience Required</p>
                <p className="text-sm text-gray-900">
                  {job.experienceMin || 0} - {job.experienceMax || 0} years
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Notice Period</p>
                <p className="text-sm text-gray-900">{job.noticePeriod || 'N/A'}</p>
              </div>
            </div>

            {/* Skills */}
            <div className="space-y-3">
              {job.primarySkills?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Primary Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(job.primarySkills) ? job.primarySkills : [job.primarySkills]).map((skill, idx) => (
                      <span key={idx} className="inline-block px-3 py-1 text-xs rounded-full" style={{ backgroundColor: "#e8f4f9", color: "#1C4E6D" }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {job.secondarySkills?.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Secondary Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {(Array.isArray(job.secondarySkills) ? job.secondarySkills : [job.secondarySkills]).map((skill, idx) => (
                      <span key={idx} className="inline-block px-3 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Compensation */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign size={20} className="text-green-600" />
              Compensation
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Compensation Range</p>
                <p className="text-sm text-gray-900 font-medium">
                  {job.compensationCurrency} {job.compensationMin?.toLocaleString() || '0'} - {job.compensationMax?.toLocaleString() || '0'}
                </p>
                {job.compensationPaytype && (
                  <p className="text-xs text-gray-500 mt-1">Pay Type: {job.compensationPaytype}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Vendor Commercials</p>
                <p className="text-sm text-gray-900">{job.vendorCommercials || 'N/A'}</p>
              </div>
            </div>
          </section>

          {/* Education & Requirements */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={20} className="text-purple-600" />
              Education & Requirements
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Education</p>
                <p className="text-sm text-gray-900">{job.education || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Preferred Institution</p>
                <p className="text-sm text-gray-900">{job.preferredInstitution || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Work Authorization</p>
                <p className="text-sm text-gray-900">{job.workAuthorization || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Visa Sponsorship</p>
                <p className="text-sm text-gray-900">{job.visaSponsorship || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Background Check</p>
                <p className="text-sm text-gray-900">{job.backgroundCheck || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Security Clearance</p>
                <p className="text-sm text-gray-900">{job.securityClearance || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">License & Certifications</p>
                <p className="text-sm text-gray-900">{job.licenseCertifications || 'N/A'}</p>
              </div>
            </div>

            {job.idealCandidateProfile && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Ideal Candidate Profile</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{job.idealCandidateProfile}</p>
              </div>
            )}
          </section>

          {/* Interview Details */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Users size={20} className="text-indigo-600" />
              Interview Details
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Interview Mode</p>
                <p className="text-sm text-gray-900">{job.interviewMode || 'N/A'}</p>
              </div>
            </div>

            {job.interviewRounds?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Interview Rounds</p>
                <div className="space-y-2">
                  {(Array.isArray(job.interviewRounds) ? job.interviewRounds : [job.interviewRounds]).map((round, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-6 h-6 flex items-center justify-center bg-indigo-100 text-indigo-600 rounded-full text-xs font-bold">
                        {idx + 1}
                      </div>
                      <span className="text-gray-900">{round}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {job.screeningQuestions?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Screening Questions</p>
                <div className="space-y-3">
                  {job.screeningQuestions.map((question, idx) => (
                    <div key={question.questionId || idx} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-900">{question.questionText}</span>
                        {question.required && (
                          <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">Required</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Type: {question.questionType}
                        {question.options?.length > 0 && (
                          <div className="mt-1">
                            Options: {question.options.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Hiring Manager */}
          {(job.hiringManagerName || job.hiringManagerEmail) && (
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Mail size={20} className="text-red-600" />
                Hiring Manager
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Name</p>
                  <p className="text-sm text-gray-900 font-medium">{job.hiringManagerName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Email</p>
                  <p className="text-sm text-gray-900">{job.hiringManagerEmail}</p>
                </div>
              </div>
            </section>
          )}

          {/* Job Description */}
          {job.jobDescription && (
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Job Description</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{job.jobDescription}</p>
              </div>
            </section>
          )}

          {/* Metadata */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar size={20} className="text-gray-600" />
              Job Metadata
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Created By</p>
                <p className="text-sm text-gray-900">{job.createdBy || 'System'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Created At</p>
                <p className="text-sm text-gray-900">{formatDate(job.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Updated By</p>
                <p className="text-sm text-gray-900">{job.updatedBy || 'System'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Updated At</p>
                <p className="text-sm text-gray-900">{formatDate(job.updatedAt)}</p>
              </div>
            </div>
          </section>
        </div>

        {/* Close Button */}
        <div className="flex gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobPreview;